import { DEFAULT_CURRENCY } from 'src/i18n/currencies/constants'
import { EN, EN_US, LOCALES_WITH_TRANSLATION_FILES, REGION_AVAILABLE_LOCALES, REGION_DEFAULT_LOCALE } from 'src/i18n/language-regions/constants'
import { getCurrencyConfigurationsByFormat, languageFromLocale, setLanguageLocaleCookie } from 'src/i18n/language-regions/utils'
import en from 'src/i18n/locales/en'
import datetimeFormats from 'src/i18n/localization-formats/date-time'
import numberFormats from 'src/i18n/localization-formats/number'
import Model from 'src/models/model'
import { createI18n } from 'vue-i18n'

export * from 'src/i18n/currencies/constants'
export * from 'src/i18n/language-regions/constants'
export * from 'src/i18n/language-regions/utils'

const i18n = createI18n({
  locale: REGION_DEFAULT_LOCALE,
  fallbackLocale: EN,
  messages: { en },
  datetimeFormats,
  numberFormats,
  // This suppresses warnings for missing translations on each locale checked, which can
  // be spammy because a warning is logged even if the translation ultimately resolves to
  // the fallbackLocale.
  silentTranslationWarn: true,
  // This suppresses warnings shown then the translation successfully falls back to the
  // fallbackLocale because the the translation was missing in the current locale.
  silentFallbackWarn: true
})
Object.defineProperty(i18n.global, 'localeLanguage', {
  get: () => languageFromLocale(i18n.global.locale),
  enumerable: true,
  configurable: false
})
export default i18n

/**
 * Triggers the following sequence that changes the language, ensure locale files are loaded,
 * and update all models with the new language's data:
 * 1. Update either the current user's preferred language or the language cookie.
 * 2. Trigger a refetch of all models, which will update `Model.context.currentLocale`.
 * 3. After the models refetch, a Promise resolver will be called with the new currentLocale
 *    and will trigger `_loadAndSetLocaleAsync()`, finishing the locale change. All other
 *    models will have also re-fetched data in the new language.
 *
 * Other functions that trigger `Model.refetchAllBaseQueries()` (e.g. `handleAuthChange()`)
 * can also trigger that watcher, meaning `_loadAndSetLocaleAsync()` will be called without
 * needing to call this function.
 */
export async function changeLanguageAndReload (language, currentUser = null) {
  if (currentUser && currentUser.isRemote && currentUser.loaded) {
    await currentUser.updatePreferredLanguage(language)
  } else {
    setLanguageLocaleCookie(language)
  }
  // Client config cache versions are not language-specific, so if we're changing languages
  // we clear the current version to prevent a validate() cache hit for the previous language.
  Model.configuration.version = null
  return new Promise(resolve => Model.refetchAllBaseQueries(resolve))
}

const loadedLocales = [EN, EN_US]
/**
 * Components should not call this directly, you probably want `changeLanguageAndReload()`
 * instead. This function ensures the locale files are loaded and sets the locale to the
 * given language-region. However, this does not update existing models' data to match
 * the new language, `changeLanguageAndReload()` handles that.
 *
 * NOTE: This assumes the given `locale` is an appropriate language-region locale for
 * the current circuit. The locale is not checked.
 *
 * @param {string} locale - The locale, preferably a language-region, to set
 * @returns {Promise<string>} - The set locale
 */
export function _loadAndSetLocaleAsync (locale) {
  if (!locale) { // No-op
    return Promise.resolve(i18n.global.locale)
  }

  if (!_.has(REGION_AVAILABLE_LOCALES, locale)) {
    // Try again with any base language
    let fallbackLocale = languageFromLocale(locale)
    if (!_.has(REGION_AVAILABLE_LOCALES, fallbackLocale)) {
      fallbackLocale = REGION_DEFAULT_LOCALE
    }
    const errorMessage = '[I18n] Unsupported locale requested, falling back to a supported locale.'
    const errorPayload = { attemptedLocale: locale, fallbackLocale }
    console.warn(errorMessage, errorPayload)
    locale = fallbackLocale
  }

  // Not every language-region has a locale file, so we determine what file, if any,
  // to actually try to fetch.
  let localeToFetch = locale
  if (!_.includes(LOCALES_WITH_TRANSLATION_FILES, localeToFetch)) {
    // If it was a language-region, check if the base language has a file.
    localeToFetch = languageFromLocale(localeToFetch)
    if (!_.includes(LOCALES_WITH_TRANSLATION_FILES, localeToFetch)) {
      localeToFetch = null
    }
  }

  if (loadedLocales.includes(locale)) {
    return Promise.resolve(setI18nLocale(locale))
  } else if (localeToFetch) {
    // About Webpack magic comments: https://webpack.js.org/api/module-methods/#magic-comments
    return import(/* webpackChunkName: "locale-[request]" */ 'src/i18n/locales/' + localeToFetch + '.js').then(
      messages => {
        i18n.global.mergeLocaleMessage(locale, messages.default)
        loadedLocales.push(locale)
        return setI18nLocale(locale)
      }
    ).catch(error => {
      const message = `[I18n] Failed to load locale ${localeToFetch}`
      console.error(message, error)
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error(message, error, { localeToFetch })
      }
    })
  } else {
    loadedLocales.push(locale)
    return Promise.resolve(setI18nLocale(locale))
  }
}

/**
 * Sets Vue.$i18n.locale and updates the html element's lang attribute. Quasar also has
 * a Quasar.lang.set() function that updates the <html> element and changes the Quasar
 * internal language pack, but we don't need it unless we want translate Quasar's
 * components and it would require installing additional language packs.
 */
function setI18nLocale (languageRegionLocale) {
  i18n.global.locale = languageRegionLocale
  updateCurrentCircuitCurrencyFormat(languageRegionLocale)
  document.querySelector('html').setAttribute('lang', languageRegionLocale)
  return i18n.global.locale
}

/**
 * The current circuit's or browser's locale's typical currency may not match
 * the currentCircuit.currency, so any time we change the circuit, we need to
 * ensure the currency format is using the currentCircuit.currency. This need
 * not be called every time the locale is changed but the circuit doesn't change,
 * but we do it anyways to ensure this gets called at least once.
 */
export async function updateCurrentCircuitCurrencyFormat (languageRegionLocale) {
  await Model.configuration.ensureLoaded()
  const currentCircuitCurrency = Model.configuration.currentCircuit?.currency || DEFAULT_CURRENCY

  i18n.global.mergeNumberFormat(languageRegionLocale, getCurrencyConfigurationsByFormat(currentCircuitCurrency))
}
